import { useEffect, useState } from 'react';
import type { MediaAsset } from '@/lib/blob-service';
import { loadMediaWithFallback, getMediaLoadingState } from '@/lib/media-loader';

export interface BaseMediaProps {
  asset: MediaAsset;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  onProgress?: (progress: number) => void;
  className?: string;
}

export interface MediaState {
  url: string | null;
  loading: boolean;
  error: Error | null;
  retrying: boolean;
}

export function useMediaLoader(asset: MediaAsset) {
  const [state, setState] = useState<MediaState>({
    url: null,
    loading: true,
    error: null,
    retrying: false
  });

  useEffect(() => {
    let mounted = true;

    async function loadMedia() {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }));
        const response = await loadMediaWithFallback(asset);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        
        if (mounted) {
          setState({ url, loading: false, error: null, retrying: false });
        }
      } catch (error) {
        if (mounted) {
          setState(prev => ({
            ...prev,
            loading: false,
            error: error instanceof Error ? error : new Error('Failed to load media'),
            retrying: false
          }));
        }
      }
    }

    loadMedia();

    return () => {
      mounted = false;
      // Cleanup any created object URLs
      if (state.url) {
        URL.revokeObjectURL(state.url);
      }
    };
  }, [asset]);

  const retry = () => {
    setState(prev => ({ ...prev, retrying: true }));
    // The effect will automatically re-run due to the state change
  };

  return { ...state, retry };
}

export function BaseMedia({ asset, onLoad, onError, className }: BaseMediaProps) {
  const { loading, error, retry } = useMediaLoader(asset);

  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 ${className}`}>
        <p className="text-sm text-destructive">Failed to load media</p>
        <button
          onClick={retry}
          className="text-sm text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return null; // Specific media components will extend this
} 